import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, Grid, Typography, Paper, useTheme, Tooltip } from '@mui/material'
import { lightBlue } from '@mui/material/colors'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { noop } from 'lodash'
import { startOfDay, isBefore, differenceInDays } from 'date-fns'
import { EventCalendar, EventList } from 'components/configured-service/accordions/EventsAccordion/components'
import { selectActionType, selectBusinessLine, selectBusinessLineType } from 'data/configured-service/configuredServiceDetailsSelectors'
import T from 'T'
import { findRecurrenceOptionByKey } from '../utils/recurrenceOptionsHelper'
import { buildRecurrenceEvents } from '../utils/eventBuilder'
import { categorizeEventChanges, filterEventsForMonth, generateEventStatusesByDay, groupEventsByDay } from '../utils/eventProcessing'

const EditServiceRecurrenceEventsPreview = ({
  open,
  startDate = new Date(),
  formData,
  defaultValues,
  recurrenceOptions,
  serviceData = {},
  existingEvents = [],
  orientation = 'horizontal',
  isNewService = false,
}) => {
  const theme = useTheme()


  // For calendar display, use today if start date is in the past (so user only sees future events)
  const displayStartDate = useMemo(() => {
    const today = startOfDay(new Date())
    const providedStartDate = startOfDay(new Date(startDate))
    return isBefore(providedStartDate, today) ? today : startDate
  }, [startDate])

  const [selectedDate, setSelectedDate] = useState(displayStartDate)

  // Store original baseline events (first render while dialog is open)
  const baselineRef = useRef(null)
  const isHorizontal = useMemo(() => orientation === 'horizontal', [orientation])

  const {
    recurrenceFrequency,
    serviceInterval: serviceIntervalKey,
    daysOfWeek: selectedDays = {},
    eventInstanceType,
    eventOccurrence,
    dayOfTheWeek,
    dayOfTheMonth,
  } = formData

  const {
    recurrenceFrequency: baselineFrequency,
    serviceInterval: baselineServiceIntervalKey,
    daysOfWeek: baselineSelectedDays = {},
    eventInstanceType: baselineEventInstanceType,
    eventOccurrence: baselineEventOccurrence,
    dayOfTheWeek: baselineDayOfTheWeek,
    dayOfTheMonth: baselineDayOfTheMonth,
  } = defaultValues

  // Convert service interval keys to objects
  const serviceInterval = findRecurrenceOptionByKey(recurrenceOptions, serviceIntervalKey)
  const baselineServiceInterval = findRecurrenceOptionByKey(recurrenceOptions, baselineServiceIntervalKey)

  const actionType = useMemo(() => selectActionType(serviceData), [serviceData])
  const businessLine = useMemo(() => selectBusinessLine(serviceData), [serviceData])
  const businessLineType = useMemo(() => selectBusinessLineType(serviceData), [serviceData])

  const serviceEventData = {
    actionType,
    businessLine,
    businessLineType,
  }

  // Calculate extended horizon when start date is in the past to ensure 90 days of future events
  const horizonDays = useMemo(() => {
    const today = startOfDay(new Date())
    const start = startOfDay(new Date(startDate))
    const daysDiff = isBefore(start, today) ? differenceInDays(today, start) : 0
    return 90 + daysDiff
  }, [startDate])

  const buildEvents = useCallback(
    (frequency, interval, daysOfWeek, events, eventData, nonWeeklyParams = {}) => {
      try {
        if (!frequency || !interval) {
          return []
        }

        return buildRecurrenceEvents({
          frequency,
          serviceIntervalData: interval,
          selectedDays: daysOfWeek,
          existingEvents: events,
          serviceEventData: eventData,
          startDate,
          horizonDays,
          nonWeeklyParams,
        })
      } catch (error) {
        return []
      }
    },
    [startDate, horizonDays, serviceEventData, existingEvents]
  )

  const currentEvents = useMemo(() => {
    if (!recurrenceFrequency || !serviceInterval) {
      return []
    }

    return buildEvents(recurrenceFrequency, serviceInterval, selectedDays, existingEvents, serviceEventData, {
      eventInstanceType,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
    })
  }, [
    buildEvents,
    recurrenceFrequency,
    serviceInterval,
    selectedDays,
    eventInstanceType,
    eventOccurrence,
    dayOfTheWeek,
    dayOfTheMonth,
    existingEvents,
    serviceEventData,
  ])

  useEffect(() => {
    if (isNewService) {
      baselineRef.current = []
      return
    }
    if (open && !baselineRef.current) {
      baselineRef.current = buildEvents(
        baselineFrequency ?? recurrenceFrequency,
        baselineServiceInterval ?? serviceInterval,
        baselineSelectedDays ?? selectedDays,
        existingEvents,
        serviceEventData
      )
    }
    if (!open) {
      baselineRef.current = null
    }
  }, [
    open,
    isNewService,
    buildEvents,
    baselineFrequency,
    baselineServiceInterval,
    baselineSelectedDays,
    recurrenceFrequency,
    serviceInterval,
    selectedDays,
    existingEvents,
    serviceEventData,
  ])

  // Generate baseline events for comparison
  const baselineEvents = useMemo(() => {
    if (isNewService) return []
    return buildEvents(
      baselineFrequency ?? recurrenceFrequency,
      baselineServiceInterval ?? serviceInterval,
      baselineSelectedDays ?? selectedDays,
      existingEvents,
      serviceEventData,
      {
        eventInstanceType: baselineEventInstanceType ?? eventInstanceType,
        eventOccurrence: baselineEventOccurrence ?? eventOccurrence,
        dayOfTheWeek: baselineDayOfTheWeek ?? dayOfTheWeek,
        dayOfTheMonth: baselineDayOfTheMonth ?? dayOfTheMonth,
      }
    )
  }, [
    isNewService,
    buildEvents,
    baselineFrequency,
    baselineServiceInterval,
    baselineSelectedDays,
    recurrenceFrequency,
    serviceInterval,
    selectedDays,
    baselineEventInstanceType,
    eventInstanceType,
    baselineEventOccurrence,
    eventOccurrence,
    baselineDayOfTheWeek,
    dayOfTheWeek,
    baselineDayOfTheMonth,
    dayOfTheMonth,
    existingEvents,
    serviceEventData,
  ])

  const eventCategories = useMemo(() => {
    return categorizeEventChanges(baselineEvents, currentEvents)
  }, [baselineEvents, currentEvents])

  const { allEvents: mockEvents } = eventCategories

  // Filter out past events from preview display
  const futureEvents = useMemo(() => {
    const today = startOfDay(new Date())
    return mockEvents.filter(event => {
      const eventDate = event.serviceDateObj || new Date(event.serviceDate)
      return !isBefore(startOfDay(eventDate), today)
    })
  }, [mockEvents])

  const currentMonthEvents = useMemo(() => {
    return filterEventsForMonth(futureEvents, selectedDate)
  }, [futureEvents, selectedDate])

  const { existingCount, newCount, removedCount } = useMemo(() => {
    return {
      existingCount: currentMonthEvents.filter(eventItem => eventItem._status === 'existing').length,
      newCount: currentMonthEvents.filter(eventItem => eventItem._status === 'new').length,
      removedCount: currentMonthEvents.filter(eventItem => eventItem._status === 'removed').length,
    }
  }, [currentMonthEvents])

  const eventsByDay = useMemo(() => {
    return groupEventsByDay(currentMonthEvents)
  }, [currentMonthEvents])

  const eventStatusesByDay = useMemo(() => {
    return generateEventStatusesByDay(currentMonthEvents)
  }, [currentMonthEvents])

  const selectedDayEvents = useMemo(() => {
    const selectedDayOfMonth = selectedDate.getDate()
    const dayEvents = eventsByDay[selectedDayOfMonth] || []
    return [...dayEvents].sort((a, b) => new Date(a.serviceDate) - new Date(b.serviceDate))
  }, [eventsByDay, selectedDate])

  const handleDateChange = newDate => {
    setSelectedDate(newDate)
  }

  const handleMonthChange = newMonth => {
    // Update selectedDate to the new month to trigger currentMonthEvents recalculation
    setSelectedDate(new Date(newMonth.getFullYear(), newMonth.getMonth(), selectedDate.getDate()))
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ backgroundColor: lightBlue[50], p: 2, border: `1px solid ${theme.palette.info.light}`, borderRadius: '4px 4px 0 0' }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
          {T.EVENTS_PREVIEW}
          <Tooltip title={T.GENERATING_EVENTS_PREVIEW} sx={{ color: 'text.secondary' }}>
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 1, backgroundColor: 'white', borderRadius: '0 0 4px 4px' }}>
        <Grid container spacing={2}>
          {/* Calendar */}
          <Grid size={{ xs: 12, md: isHorizontal ? 6 : 12 }}>
            <EventCalendar
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              onMonthChange={handleMonthChange}
              isLoading={false}
              eventsByDay={eventsByDay}
              eventStatusesByDay={eventStatusesByDay}
            />
          </Grid>

          {/* Event List */}
          <Grid size={{ xs: 12, md: isHorizontal ? 6 : 12 }}>
            <Box
              sx={{
                border: '2px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                p: 1,
              }}
            >
              <Typography variant="body2" fontWeight={500} mb={1}>
                {T.EVENTS}
              </Typography>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {!isNewService && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 18, height: 18, backgroundColor: theme.palette.primary.main, borderRadius: '2px' }} />
                    <Typography variant="caption">Existing -{existingCount}</Typography>
                  </Box>
                )}
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 18, height: 18, backgroundColor: theme.palette.info.light, borderRadius: '2px' }} />
                  <Typography variant="caption">New -{newCount}</Typography>
                </Box>
                {!isNewService && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 18, height: 18, backgroundColor: theme.palette.grey[300], borderRadius: '2px' }} />
                    <Typography variant="caption">Removed -{removedCount}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <EventList
              selectedDayEvents={selectedDayEvents}
              control={{}}
              serviceActionsById={{}}
              getAvailableActionsForEvent={noop}
              onEdit={noop}
              onDelete={noop}
              onUndoDelete={noop}
              onAddEvent={noop}
              isDraft={false}
              hideAddEventButton
              isPreview
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

EditServiceRecurrenceEventsPreview.propTypes = {
  open: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  formData: PropTypes.shape({
    recurrenceFrequency: PropTypes.string,
    serviceInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    daysOfWeek: PropTypes.object,
    eventInstanceType: PropTypes.string,
    eventOccurrence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dayOfTheWeek: PropTypes.string,
    dayOfTheMonth: PropTypes.number,
  }).isRequired,
  defaultValues: PropTypes.shape({
    recurrenceFrequency: PropTypes.string,
    serviceInterval: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    daysOfWeek: PropTypes.object,
    eventInstanceType: PropTypes.string,
    eventOccurrence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dayOfTheWeek: PropTypes.string,
    dayOfTheMonth: PropTypes.number,
  }).isRequired,
  recurrenceOptions: PropTypes.array.isRequired,
  serviceData: PropTypes.object,
  existingEvents: PropTypes.array,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  isNewService: PropTypes.bool,
}

export default EditServiceRecurrenceEventsPreview
